// @flow

import React from 'react';
import { observer } from 'mobx-react';
import { Element } from 'react-scroll';
import { StickyContainer, Sticky } from 'react-sticky';
import FacebookShareButton from '~/components/global/FacebookShareButton';
import TwitterShareButton from '~/components/global/TwitterShareButton';
import Masonry from 'react-masonry-component';
import moment from 'moment';
import Helmet from 'react-helmet';
import { css, before, after } from 'glamor';
import { Col, Row, Clearfix, Grid } from 'react-bootstrap';

import GameDetails from '~/components/content/game/GameDetails';
import Markdown from '~/components/global/Markdown';
import SectionScroller from '~/components/global/SectionScroller';
import ScoreBox from '~/components/global/ScoreBox';
import Title from '~/components/global/Title';
import Gallery from '~/components/global/Gallery';
import Contributor from '~/components/global/Contributor';
import type { Review as ReviewType } from '~/types';

const WEBSITE_BASE_URL = (function() {
    if (typeof location !== 'undefined') {
        return location.href;
    }

    return process.env.NODE_ENV === 'production' ?
        'https://www.supagemu.com' :
        'http://localhost:4002';
})();

const sections = [
    { label: 'game', name: 'game-section' },
    { label: 'review', name: 'review-section' },
    { label: 'scores', name: 'scores-section' },
    { label: 'package', name: 'package-section' },
    { label: 'images', name: 'screenshots-section' },
];

const categories = [
    { category: 'visuals', icon: 'film' },
    { category: 'soundtrack', icon: 'music' },
    { category: 'controls', icon: 'sliders' },
    { category: 'gameplay', icon: 'gamepad' },
    { category: 'story', icon: 'file-text-o' },
    { category: 'innovation', icon: 'lightbulb-o' },
    { category: 'length', icon: 'clock-o' },
];

const reviewRule = css({
    marginTop: '20px',
    textAlign: 'justify',
    fontSize: '15px',
    fontWeight: 300,

    '& ul': {
        lineHeight: '25px',
    },

    '& p': {
        padding: '15px',
        lineHeight: '25px',

        '@media(max-width: 768px)': {
            padding: 0,
        },
    },

    '&p + p': {
        paddingTop: 0,
        paddingBottom: 0,
    },

    '& h4': {
        position: 'relative',
        zIndex: -1,
        marginLeft: '25px',

        paddingLeft: '15px',
        paddingTop: '15px',
        paddingBottom: '15px',

        color: '#e44122',
        fontStyle: 'italic',
        fontWeight: 600,
        fontSize: '1.6em',
        letterSpacing: '1px',
        wordSpacing: '5px',

        overflow: 'auto',

        '&:before': {
            backgroundColor: '#b7b7b7',
            position: 'absolute',
            bottom: '7px',
            top: '7px',
            left: 0,
            content: '""',
            width: '3px',
        },
    },

    '& img': {
        width: '250px',
        height: '200px',
        margin: '25px',

        '@media(max-width: 768px)': {
            display: 'block',
            margin: 'auto',
            marginBottom: '25px',
            marginTop: '25px',
        },

        '@media(min-width: 769px)': {
            '&.img-right': {
                float: 'right',
                marginLeft: '40px',
                marginBottom: '10px',
            },

            '&.img-left': {
                float: 'left',
                marginRight: '40px',
                marginBottom: '10px',
            },
        },
    },
});

const detailsRule = css({
    marginBottom: '20px',
});

const sectionsRule = css({
    zIndex: 1,
});

const scoreBoxRule = css({
    marginTop: '15px',
});

const finalScoresRule = css({
    fontWeight: 800,
    fontSize: '1.5em',
    textAlign: 'center',
});

const sectionRule = css({
    position: 'relative',
    zIndex: 0,
});

const galleryRule = css({
    height: '600px',
    paddingLeft: 0,
});

const galleryTitleRule = css({
    paddingTop: '30px',
    paddingBottom: '15px',
});

const galleryImageRule = css({
    margin: '15px',
    marginRight: '30px',
    marginleft: 0,
    maxWidth: '330px',
    cursor: 'pointer',

    '@media(max-width: 768px)': {
        marginRight: 0,
        marginLeft: 0,
        maxWidth: '100%',
    },
});

const imageListRule = css({
    listStyle: 'none',
});

const authorRule = css({
    color: '#676767',
    fontSize: '0.9em',
});

const reviewDataRule = css({
    borderLeft: '4px solid #e44122',
    marginBottom: '20px',
});

const blockquoteRule = css(
    {
        border: 0,
        margin: 0,
        background: '#f9f9f9',
        padding: '0.5em 10px',
        quotes: '"“" "”" "‘" "’"',
        fontSize: '1.1em',
        lineHeight: '40px',
    },
    before({
        fontFamily: 'serif',
        color: '#e44122',
        content: 'open-quote',
        fontSize: '4em',
        lineHeight: '0.1em',
        marginRight: '0.2em',
        verticalAlign: '-0.3em',
    }),
    after({
        fontFamily: 'serif',
        color: '#e44122',
        content: 'close-quote',
        fontSize: '4em',
        lineHeight: '0.1em',
        marginLeft: '0.2em',
        position: 'absolute',
        bottom: '-0.1em',
    })
);

const shareButton = css({
    marginLeft: '10px',
    marginBottom: '10px',
});

const shareButtons = css({
    float: 'right',
    marginRight: '-15px',
});

const avatarRule = css({
    width: '60px',
    height: '60px',
});

@observer
export default class ReviewMain extends React.Component {
    props: {
        addBox: () => void,
        boxesMaxHeight: number,
        closePackageGallery: () => void,
        closeScreenshotsGallery: () => void,
        isPackageGalleryOpen: boolean,
        isScreenshotsGalleryOpen: boolean,
        openPackageGallery: () => void,
        openScreenshotsGallery: () => void,
        packageCurrentImage: number,
        review: ReviewType,
        screenshotsCurrentImage: number,
    };

    render() {
        const {
            addBox,
            boxesMaxHeight,
            closePackageGallery,
            closeScreenshotsGallery,
            isPackageGalleryOpen,
            isScreenshotsGalleryOpen,
            openPackageGallery,
            openScreenshotsGallery,
            packageCurrentImage,
            review,
            screenshotsCurrentImage,
        } = this.props;

        if (!review) {
            return null;
        }

        const publicationDate = moment(new Date(review.publishedAt)).format(
            'DD MMMM YYYY'
        );

        return (
            <StickyContainer>
                <Helmet
                    title={ `${review.game.name} review` }
                    meta={ [
                        {
                            name: 'description',
                            content: `${review.game.name} on ${review.platform.name} review`,
                        },
                        {
                            property: 'og:url',
                            content: `${WEBSITE_BASE_URL}/reviews/${review.slug}`,
                        },
                        {
                            property: 'og:type',
                            content: 'article',
                        },
                        {
                            property: 'og:title',
                            content: 'supagemu.com',
                        },
                        {
                            property: 'og:description',
                            content: `${review.game.name} review: ${review.headline}`,
                        },
                        {
                            property: 'og:image',
                            content: `${review.game.image.secure_url}`,
                        },
                    ] }
                />

                <Grid>
                    <div { ...shareButtons }>
                        <TwitterShareButton
                            url={ `https://www.supagemu.com/reviews/${review.slug}` }
                            message={ review.headline + ' #supagemu' }
                            { ...shareButton }
                        />
                        <FacebookShareButton
                            url={ `https://www.supagemu.com/reviews/${review.slug}` }
                            { ...shareButton }
                        />
                    </div>

                    <Clearfix />

                    <Element name="game-section" { ...sectionRule }>
                        <Row { ...detailsRule }>
                            <GameDetails game={ review.game } review={ review } />
                        </Row>

                        <Row { ...reviewDataRule }>
                            <Col md={ 3 }>
                                <div { ...authorRule }>
                                    <Contributor
                                        user={ review.author }
                                        title="Author"
                                        subtitle={ `Published ${publicationDate}` }
                                        displayDetails
                                        avatarRule={ avatarRule }
                                    />
                                </div>
                            </Col>
                            <Col md={ 9 }>
                                <blockquote { ...blockquoteRule }>
                                    {review.headline}
                                </blockquote>
                            </Col>
                        </Row>

                    </Element>

                    <Sticky style={{ zIndex: 1 }}>
                        {({ style }) => (
                            <Row { ...sectionsRule } style={ style }>
                                <SectionScroller sections={ sections } />
                            </Row>
                        )}
                    </Sticky>

                    <Element name="review-section" { ...sectionRule }>
                        <Markdown
                            styleRule={ reviewRule }
                            content={ review.content.extended.md }
                        />
                    </Element>

                    <Element name="scores-section" { ...sectionRule }>
                        <div { ...scoreBoxRule }>
                            {categories.map(c => (
                                <ScoreBox
                                    key={ c.category }
                                    icon={ c.icon }
                                    category={ c.category }
                                    text={ review[c.category].text.md }
                                    score={ review[c.category].score }
                                />
                            ))}
                        </div>

                        <h3 { ...finalScoresRule }>
                            {'FINAL SCORES'}
                        </h3>

                        <Row>
                            <Col md={ 6 }>
                                <ScoreBox
                                    final
                                    key={ 'objectiveScore' }
                                    category={ 'objective score' }
                                    text={ review.objectiveScore.text.md }
                                    score={ review.objectiveScore.score }
                                    ref={ addBox }
                                    height={ boxesMaxHeight }
                                />
                            </Col>
                            <Col md={ 6 }>
                                <ScoreBox
                                    final
                                    key={ 'subjectiveScore' }
                                    category={ 'subjective score' }
                                    text={ review.subjectiveScore.text.md }
                                    score={ review.subjectiveScore.score }
                                    ref={ addBox }
                                    height={ boxesMaxHeight }
                                />
                            </Col>
                        </Row>
                    </Element>

                    <Element name="package-section" { ...sectionRule }>
                        <div { ...galleryTitleRule }>
                            <Title
                                boldText="PACKAGE"
                                normalText=" / GAME BOX, INSTRUCTIONS"
                            />
                        </div>

                        <Masonry { ...galleryRule } elementType="ul">
                            {review.package ?
                                review.package.map((img, index) => (
                                      <li key={ index } { ...imageListRule }>
                                          <img
                                              onClick={ openPackageGallery.bind(
                                                  this,
                                                  index
                                              ) }
                                              { ...galleryImageRule }
                                              src={ img.secure_url }
                                          />
                                      </li>
                                  )) :
                                null}
                        </Masonry>

                        <Gallery
                            images={ review.package.map(i => ({
                                src: i.secure_url,
                            })) }
                            isOpen={ isPackageGalleryOpen }
                            onClose={ closePackageGallery.bind(this) }
                            currentImage={ packageCurrentImage }
                        />
                    </Element>

                    <Element name="screenshots-section" { ...sectionRule }>
                        <div { ...galleryTitleRule }>
                            <Title
                                boldText="SCREENSHOTS"
                                normalText=" / ARTWORKS"
                            />
                        </div>

                        <Masonry { ...galleryRule } elementType="ul">
                            {review.screenshots ?
                                review.screenshots.map((img, index) => (
                                      <li key={ index } { ...imageListRule }>
                                          <img
                                              onClick={ openScreenshotsGallery.bind(
                                                  this,
                                                  index
                                              ) }
                                              { ...galleryImageRule }
                                              src={ img.secure_url }
                                          />
                                      </li>
                                  )) :
                                null}
                        </Masonry>

                        <Gallery
                            images={ review.screenshots.map(i => ({
                                src: i.secure_url,
                            })) }
                            isOpen={ isScreenshotsGalleryOpen }
                            onClose={ closeScreenshotsGallery.bind(this) }
                            currentImage={ screenshotsCurrentImage }
                        />

                    </Element>
                </Grid>

            </StickyContainer>
        );
    }
}



// WEBPACK FOOTER //
// ./src/components/content/review/ReviewMain.js